import { ReturnActionId } from "@redotech/redo-return-core-common/return-actions/registry";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { viewTypes } from "@redotech/redo-views-common/views";
import {
  ClaimSystemView,
  NEEDS_ATTENTION_FOLDER_NAME,
  RecycleSystemView,
  ReturnSystemView,
  StaticClaimViews,
  StaticRecycleViews,
  StaticSystemViews,
  StaticWarrantyViews,
  WarrantySystemView,
} from "./default-return-views";

export type NeedsActionRecordType =
  | "returns"
  | "claims"
  | "warranties"
  | "recycle"
  | "withdrawals";

/** Where the records that need action come from. */
export type NeedsActionSource =
  | { kind: "view"; view: string }
  | { kind: "withdrawal-list"; list: "needs_action" };

/**
 * What the merchant does about them: a bulk action, a look at each record, or
 * asking Redo. Only `ask-redo` entries offer "Resolve this for me", so mark an
 * entry that way only once Ask Redo can actually fix it.
 */
export type NeedsActionResolution =
  | { kind: "bulk-action"; action: ReturnActionId }
  | { kind: "review" }
  | { kind: "ask-redo"; prompt: string };

export interface NeedsActionEntry {
  id: string;
  source: NeedsActionSource;
  resolution: NeedsActionResolution;
}

const REVIEW: NeedsActionResolution = { kind: "review" };
const PROCESS: NeedsActionResolution = {
  kind: "bulk-action",
  action: "process",
};

function viewEntry(
  view: string,
  resolution: NeedsActionResolution = REVIEW,
): NeedsActionEntry {
  return { id: view, source: { kind: "view", view }, resolution };
}

/**
 * The views that need the merchant's attention, per record type, in the
 * order the home page ranks them and the "Needs attention" folder lists
 * them. Returns waiting on processing, on their remaining items, or on
 * remit are processed in bulk; the process action skips any that aren't
 * eligible.
 */
export const NEEDS_ACTION = {
  returns: [
    viewEntry(ReturnSystemView.STUCK_IN_TRANSIT),
    viewEntry(ReturnSystemView.AWAITING_PROCESSING, PROCESS),
    viewEntry(ReturnSystemView.PARTIALLY_PROCESSED, PROCESS),
    viewEntry(ReturnSystemView.AWAITING_REMIT, PROCESS),
    viewEntry(ReturnSystemView.FAILED_RMA_CREATION),
    viewEntry(ReturnSystemView.LABEL_CREATION_FAILED),
    viewEntry(ReturnSystemView.REFUND_PAYMENT_FAILED),
    viewEntry(ReturnSystemView.RESTOCK_FAILED),
    viewEntry(ReturnSystemView.OUT_OF_STOCK),
    viewEntry(ReturnSystemView.TRACKING_EXCEPTION),
    viewEntry(ReturnSystemView.DELAYED_IN_CUSTOMS),
    viewEntry(ReturnSystemView.MISSING_CUSTOMS_INFO),
  ],
  claims: [
    viewEntry(ClaimSystemView.PARTIALLY_PROCESSED),
    viewEntry(ClaimSystemView.FAILED_RMA_CREATION),
    viewEntry(ClaimSystemView.LABEL_CREATION_FAILED),
    viewEntry(ClaimSystemView.REFUND_PAYMENT_FAILED),
    viewEntry(ClaimSystemView.TRACKING_EXCEPTION),
    viewEntry(ClaimSystemView.DELAYED_IN_CUSTOMS),
  ],
  warranties: [
    viewEntry(WarrantySystemView.PARTIALLY_PROCESSED),
    viewEntry(WarrantySystemView.FAILED_RMA_CREATION),
    viewEntry(WarrantySystemView.LABEL_CREATION_FAILED),
    viewEntry(WarrantySystemView.REFUND_PAYMENT_FAILED),
    viewEntry(WarrantySystemView.TRACKING_EXCEPTION),
    viewEntry(WarrantySystemView.DELAYED_IN_CUSTOMS),
  ],
  recycle: [
    viewEntry(RecycleSystemView.PARTIALLY_PROCESSED),
    viewEntry(RecycleSystemView.FAILED_RMA_CREATION),
    viewEntry(RecycleSystemView.LABEL_CREATION_FAILED),
    viewEntry(RecycleSystemView.REFUND_PAYMENT_FAILED),
  ],
  withdrawals: [
    {
      id: "needs-action",
      source: { kind: "withdrawal-list", list: "needs_action" },
      resolution: REVIEW,
    },
  ],
} satisfies Record<NeedsActionRecordType, readonly NeedsActionEntry[]>;

/** The view names of a type's entries, which is what the folder seed and the needs-action filter use. */
export function needsActionViewNames(
  entries: readonly NeedsActionEntry[],
): string[] {
  return entries.flatMap((entry) =>
    entry.source.kind === "view" ? [entry.source.view] : [],
  );
}

function needsAttentionFolder(entries: readonly NeedsActionEntry[]) {
  return [
    {
      name: NEEDS_ATTENTION_FOLDER_NAME,
      viewNames: needsActionViewNames(entries),
    },
  ];
}

export const DEFAULT_RETURN_FOLDERS = needsAttentionFolder(
  NEEDS_ACTION.returns,
);
export const DEFAULT_CLAIM_FOLDERS = needsAttentionFolder(NEEDS_ACTION.claims);
export const DEFAULT_WARRANTY_FOLDERS = needsAttentionFolder(
  NEEDS_ACTION.warranties,
);
export const DEFAULT_RECYCLE_FOLDERS = needsAttentionFolder(
  NEEDS_ACTION.recycle,
);

const STATIC_VIEWS_FOR_RETURN_TYPE: Record<
  ReturnTypeEnum,
  {
    entries: readonly NeedsActionEntry[];
    views: Record<string, viewTypes["returns"]>;
  }
> = {
  [ReturnTypeEnum.RETURN]: {
    entries: NEEDS_ACTION.returns,
    views: StaticSystemViews,
  },
  [ReturnTypeEnum.CLAIM]: {
    entries: NEEDS_ACTION.claims,
    views: StaticClaimViews,
  },
  [ReturnTypeEnum.WARRANTY]: {
    entries: NEEDS_ACTION.warranties,
    views: StaticWarrantyViews,
  },
  [ReturnTypeEnum.RECYCLE]: {
    entries: NEEDS_ACTION.recycle,
    views: StaticRecycleViews,
  },
  [ReturnTypeEnum.WITHDRAWAL]: { entries: NEEDS_ACTION.withdrawals, views: {} },
};

/**
 * The filters of one entry's system view, which hold for every team whether
 * or not its saved copy of the view exists. Undefined for an entry that is
 * not a view.
 */
export function needsActionEntryFilters(
  type: ReturnTypeEnum,
  entry: NeedsActionEntry,
): AdvancedFilterData[] | undefined {
  if (entry.source.kind !== "view") {
    return undefined;
  }
  return STATIC_VIEWS_FOR_RETURN_TYPE[type].views[entry.source.view]?.filters;
}

/**
 * The filter list of each needs-action view of a type. A record needs action
 * when it matches any one of these lists.
 */
export function needsActionViewFilters(
  type: ReturnTypeEnum,
): AdvancedFilterData[][] {
  return STATIC_VIEWS_FOR_RETURN_TYPE[type].entries.flatMap((entry) => {
    const filters = needsActionEntryFilters(type, entry);
    return filters ? [filters] : [];
  });
}
